import React, { useEffect, useState } from 'react';
import { XIcon, AlertCircleIcon, PlusIcon, LoaderIcon } from 'lucide-react';
import { getTeacherStudents } from '../services/studentService';
import { useAuth } from '../contexts/AuthContext';
interface AddAlertModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (alert: {
    studentId: string;
    studentName: string;
    reason: string;
    details: string;
    practiceHours: number;
    alertType?: string;
    threshold?: number;
  }) => Promise<void>;
  darkMode: boolean;
  studentsList?: Array<{
    id: string;
    name: string;
  }>;
  onCreateStudent?: () => void;
}
export const AddAlertModal: React.FC<AddAlertModalProps> = ({
  isOpen,
  onClose,
  onSave,
  darkMode,
  studentsList: propStudentsList,
  onCreateStudent
}) => {
  const {
    currentUser
  } = useAuth();
  const [selectedStudentId, setSelectedStudentId] = useState<string>('');
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [alertType, setAlertType] = useState('custom');
  const [threshold, setThreshold] = useState<number>(0);
  const [practiceHours, setPracticeHours] = useState<number>(0);
  // Real students from Firestore
  const [students, setStudents] = useState<Array<{
    id: string;
    name: string;
  }>>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [errors, setErrors] = useState<{
    student?: string;
    reason?: string;
    save?: string;
  }>({});
  // Fetch real students when modal opens
  useEffect(() => {
    const fetchStudents = async () => {
      if (!isOpen || !currentUser?.uid) return;
      // Use prop students if provided, otherwise fetch from Firestore
      if (propStudentsList && propStudentsList.length > 0) {
        setStudents(propStudentsList);
        return;
      }
      try {
        setStudentsLoading(true);
        const teacherStudents = await getTeacherStudents(currentUser.uid);
        const studentOptions = teacherStudents.map(student => ({
          id: student.studentId || student.id,
          name: student.name
        }));
        setStudents(studentOptions);
      } catch (error) {
        console.error('Error fetching students:', error);
        setStudents([]);
      } finally {
        setStudentsLoading(false);
      }
    };
    fetchStudents();
  }, [isOpen, currentUser?.uid, propStudentsList]);
  // Reset form when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSelectedStudentId('');
      setReason('');
      setDetails('');
      setAlertType('custom');
      setThreshold(0);
      setPracticeHours(0);
      setErrors({});
      setIsSaving(false);
    }
  }, [isOpen]);
  // Auto-populate reason based on alert type
  useEffect(() => {
    if (alertType !== 'custom') {
      switch (alertType) {
        case 'practice':
          setReason('Low practice hours');
          break;
        case 'attendance':
          setReason('Low attendance rate');
          break;
        case 'progress':
          setReason('Behind on assignments');
          break;
        default:
          setReason('');
      }
    }
  }, [alertType]);
  if (!isOpen) return null;
  const handleSave = async () => {
    // Clear previous errors
    setErrors({});
    // Validation
    const newErrors: {
      student?: string;
      reason?: string;
    } = {};
    if (!selectedStudentId) {
      newErrors.student = 'Please select a student';
    }
    if (!reason.trim()) {
      newErrors.reason = 'Please enter an alert title';
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    // Find selected student
    const selectedStudent = students.find(s => s.id === selectedStudentId);
    if (!selectedStudent) {
      setErrors({
        student: 'Selected student not found'
      });
      return;
    }
    try {
      setIsSaving(true);
      console.log('🔵 AddAlertModal: Saving alert...', {
        studentId: selectedStudent.id,
        studentName: selectedStudent.name,
        reason: reason.trim()
      });
      await onSave({
        studentId: selectedStudent.id,
        studentName: selectedStudent.name,
        reason: reason.trim(),
        details: details.trim(),
        practiceHours: practiceHours,
        alertType: alertType,
        threshold: threshold > 0 ? threshold : undefined
      });
      console.log('✅ AddAlertModal: Alert saved successfully');
      handleCancel();
    } catch (error) {
      console.error('❌ AddAlertModal: Error saving alert:', error);
      setErrors({
        save: error instanceof Error ? error.message : 'Failed to save alert. Please try again.'
      });
    } finally {
      setIsSaving(false);
    }
  };
  const handleCancel = () => {
    setSelectedStudentId('');
    setReason('');
    setDetails('');
    setAlertType('custom');
    setThreshold(0);
    setPracticeHours(0);
    setErrors({});
    setIsSaving(false);
    onClose();
  };
  const handleStudentChange = (value: string) => {
    if (value === 'create_new' && onCreateStudent) {
      onCreateStudent();
      return;
    }
    setSelectedStudentId(value);
    setErrors(prev => ({
      ...prev,
      student: undefined
    }));
  };
  return <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-id="element-256">
      <div className={`${darkMode ? 'bg-navy-800' : 'bg-white'} rounded-xl p-6 w-full max-w-md shadow-2xl max-h-[90vh] overflow-y-auto`} onClick={e => e.stopPropagation()} data-id="element-257">
        {/* Header */}
        <div className="flex justify-between items-center mb-6" data-id="element-258">
          <div className="flex items-center gap-3" data-id="element-259">
            <div className={`p-2 rounded-lg ${darkMode ? 'bg-orange-500/20' : 'bg-orange-100'}`} data-id="element-260">
              <AlertCircleIcon className="h-5 w-5 text-orange-500" data-id="element-261" />
            </div>
            <h3 className={`text-xl font-bold ${darkMode ? 'text-white' : 'text-gray-800'}`} data-id="element-262">
              Add Attention Alert
            </h3>
          </div>
          <button onClick={handleCancel} disabled={isSaving} className={`${darkMode ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-gray-700'} transition-colors ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-263">
            <XIcon className="h-5 w-5" data-id="element-264" />
          </button>
        </div>

        {/* Form */}
        <div className="space-y-4" data-id="element-265">
          {/* Alert Type */}
          <div data-id="element-266">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-267">
              Alert Type
            </label>
            <select value={alertType} onChange={e => setAlertType(e.target.value)} disabled={isSaving} className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink ${darkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-268">
              <option value="custom" data-id="element-269">Custom Alert</option>
              <option value="practice" data-id="element-270">Practice Hours</option>
              <option value="attendance" data-id="element-271">Attendance Rate</option>
              <option value="progress" data-id="element-272">Progress/Assignments</option>
            </select>
          </div>

          {/* Student Select */}
          <div data-id="element-273">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-274">
              Student <span className="text-red-500" data-id="element-275">*</span>
            </label>
            {studentsLoading ? <div className={`w-full px-3 py-2 rounded-lg flex items-center justify-center ${darkMode ? 'bg-navy-900' : 'bg-gray-50'}`} data-id="element-276">
                <LoaderIcon className="h-4 w-4 animate-spin text-deep-pink mr-2" data-id="element-277" />
                <span className={darkMode ? 'text-gray-400' : 'text-gray-500'} data-id="element-278">
                  Loading students...
                </span>
              </div> : <select value={selectedStudentId} onChange={e => handleStudentChange(e.target.value)} disabled={isSaving} className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink ${darkMode ? 'bg-navy-900 text-white' : 'bg-gray-50 text-gray-900'} ${errors.student ? 'ring-2 ring-red-500' : ''} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-279">
                <option value="" data-id="element-280">Select a student...</option>
                {onCreateStudent && <option value="create_new" className="font-medium" data-id="element-281">
                    + Create New Student
                  </option>}
                {students.map(student => <option key={student.id} value={student.id} data-id="element-282">
                    {student.name}
                  </option>)}
              </select>}
            {errors.student && <p className="text-red-500 text-xs mt-1" data-id="element-283">{errors.student}</p>}
          </div>

          {/* Alert Title / Reason */}
          <div data-id="element-284">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-285">
              Alert Title / Reason <span className="text-red-500" data-id="element-286">*</span>
            </label>
            <input type="text" value={reason} onChange={e => {
            setReason(e.target.value);
            setErrors(prev => ({
              ...prev,
              reason: undefined
            }));
          }} disabled={isSaving} placeholder="e.g., Low practice hours, Missed last lesson" className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-gray-50 text-gray-900 placeholder-gray-500'} ${errors.reason ? 'ring-2 ring-red-500' : ''} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-287" />
            {errors.reason && <p className="text-red-500 text-xs mt-1" data-id="element-288">{errors.reason}</p>}
          </div>

          {/* Threshold (for auto-alerts) */}
          {alertType !== 'custom' && <div data-id="element-289">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-290">
                Threshold (Optional)
              </label>
              <input type="number" value={threshold} onChange={e => setThreshold(Number(e.target.value))} disabled={isSaving} placeholder={alertType === 'practice' ? 'Min hours per week' : alertType === 'attendance' ? 'Min attendance %' : 'Min completion %'} className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-gray-50 text-gray-900 placeholder-gray-500'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-291" />
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} data-id="element-292">
                Alert will trigger when below this threshold
              </p>
            </div>}

          {/* Practice Hours (for context) */}
          {alertType === 'practice' && <div data-id="element-293">
              <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-294">
                Current Practice Hours
              </label>
              <input type="number" step="0.5" value={practiceHours} onChange={e => setPracticeHours(Number(e.target.value))} disabled={isSaving} placeholder="Hours per week" className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-gray-50 text-gray-900 placeholder-gray-500'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-295" />
            </div>}

          {/* Details / Notes */}
          <div data-id="element-296">
            <label className={`block text-sm font-medium mb-2 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`} data-id="element-297">
              Details / Notes
            </label>
            <textarea value={details} onChange={e => setDetails(e.target.value)} disabled={isSaving} placeholder="Add any additional context or notes..." rows={4} className={`w-full px-3 py-2 rounded-lg border-none focus:ring-2 focus:ring-deep-pink resize-none ${darkMode ? 'bg-navy-900 text-white placeholder-gray-400' : 'bg-gray-50 text-gray-900 placeholder-gray-500'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-298" />
          </div>

          {/* Save Error Message */}
          {errors.save && <div className={`p-3 rounded-lg ${darkMode ? 'bg-red-500/10 border border-red-500/30' : 'bg-red-50 border border-red-200'}`} data-id="element-299">
              <p className="text-red-500 text-sm" data-id="element-300">{errors.save}</p>
            </div>}
        </div>

        {/* Actions */}
        <div className="flex gap-3 mt-6" data-id="element-301">
          <button onClick={handleCancel} disabled={isSaving} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${darkMode ? 'bg-navy-700 text-white hover:bg-navy-600' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'} ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`} data-id="element-302">
            Cancel
          </button>
          <button onClick={handleSave} disabled={studentsLoading || isSaving} className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors border flex items-center justify-center gap-2 ${studentsLoading || isSaving ? 'opacity-50 cursor-not-allowed' : ''} ${darkMode ? 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/10' : 'border-[#10B981] text-[#10B981] hover:bg-[#10B981]/5'}`} data-id="element-303">
            {isSaving && <LoaderIcon className="h-4 w-4 animate-spin" data-id="element-304" />}
            {isSaving ? 'Saving...' : 'Save Alert'}
          </button>
        </div>
      </div>
    </div>;
};